/**
 * Dashboard ranking tables (plan §3.3 · demo lines 914–933).
 *
 * Table A is "top N by return". Table B is "return at lowest variance", and its
 * two-stage shape is the point: risk is scored over the RETURN-LEADER POOL, not
 * over the whole filtered universe, so the normalised score answers "least risky
 * of the winners" rather than "least risky overall". Scoring the universe and
 * then truncating would give a different — and much less useful — table, which
 * is exactly the query-relative hazard §6.5 exists to name.
 */
import type { Ticker, WindowKey } from '@meridian/contracts';

import { computeRiskScores, type RiskInput } from '../analytics/risk-score';

import { sortRows, type SortableRow } from './sort-rows';

/** Rank count is clamped into this band (demo line 831). */
export const RANK_COUNT_MIN = 5;
export const RANK_COUNT_MAX = 40;
export const RANK_COUNT_DEFAULT = 20;
/** Leader pool for table B: at least this many, or twice the rank count. */
export const LEADER_POOL_MIN = 40;
export const LEADER_POOL_FACTOR = 2;

export function rankCount(requested?: number): number {
  const n = requested ?? RANK_COUNT_DEFAULT;
  if (!Number.isFinite(n)) return RANK_COUNT_DEFAULT;
  return Math.max(RANK_COUNT_MIN, Math.min(RANK_COUNT_MAX, n));
}

type RankableRow = SortableRow & RiskInput;

export function topByReturn<T extends RankableRow>(
  rows: readonly T[],
  window: WindowKey,
  count: number,
): readonly T[] {
  if (count <= 0) return [];
  /* Rows with no return for the window are EXCLUDED, not merely sorted last.
     `sortRows` pushes them to the tail, but slicing the first N then handed a
     rank — and a place in a panel titled "Top N · Total Return" — to rows that
     were never ordered by the metric. Reachable on any live backfill gap: a
     recent listing has no 3Y return. A short table is honest; a padded one is
     not. */
  const ranked = rows.filter((row) => {
    const ret = row.ret[window];
    return ret !== null && Number.isFinite(ret);
  });
  return sortRows(ranked, { k: 'ret', d: -1 }, { window }).slice(0, count);
}

export interface LowestVarianceRanking<T> {
  readonly rows: readonly T[];
  /** Scores over the LEADER POOL — the set they were normalised against. */
  readonly riskScores: ReadonlyMap<Ticker, number>;
}

export function returnAtLowestVariance<T extends RankableRow>(
  rows: readonly T[],
  window: WindowKey,
  count: number,
): LowestVarianceRanking<T> {
  const poolSize = Math.max(count * LEADER_POOL_FACTOR, LEADER_POOL_MIN);
  const leaders = topByReturn(rows, window, poolSize);
  const riskScores = computeRiskScores(leaders, window);
  return {
    rows: sortRows(
      leaders.filter((row) => riskScores.has(row.ticker)),
      { k: 'risk', d: 1 },
      { window, riskScores },
    ).slice(0, count),
    riskScores,
  };
}
